import axios, { AxiosInstance } from 'axios';
import { useAuthStore } from '@/store/authStore';
import { buildTechnicianPortalUrl } from '@/utils/portalUrl';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://api.wrenchlab.net';

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Inject tenant JWT + optional staff JWT on every request
apiClient.interceptors.request.use((config) => {
  const { token, staffToken } = useAuthStore.getState();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (staffToken) {
    config.headers['X-Staff-Token'] = staffToken;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data;

    if (status === 401) {
      const auth = useAuthStore.getState();
      const technicianRoles = ['Technician', 'SeniorTechnician', 'Apprentice', 'Student', 'Intern'];
      const isTechnicianSession = !!auth.staffRole && technicianRoles.includes(auth.staffRole);
      const technicianSignIn = buildTechnicianPortalUrl(auth.tenantSlug) ?? '/technician-login';

      // Staff JWT expired/missing but tenant session is still valid — re-auth as staff only
      if (data?.error === 'STAFF_AUTH_REQUIRED') {
        if (isTechnicianSession || window.location.pathname.startsWith('/tech')) {
          auth.clearAuth();
          window.location.href = technicianSignIn;
        } else {
          auth.clearStaffSession();
          if (!window.location.pathname.startsWith('/staff-login')) window.location.href = '/staff-login';
        }
        return Promise.reject(error);
      }

      const publicPaths = ['/login', '/forgot-password', '/reset-password', '/staff-login', '/workspace-login', '/technician-login'];
      const onPublicPage = publicPaths.some((p) => window.location.pathname.startsWith(p));
      if (!onPublicPage) {
        const expiredToken = useAuthStore.getState().token;
        if (expiredToken) {
          fetch(`${BASE_URL}/api/auth/logout`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${expiredToken}` },
          }).catch(() => {});
        }
        auth.clearAuth();
        // Redirect to appropriate sign-in page based on current host
        const host = window.location.hostname.toLowerCase();
        const isPortal = host.startsWith('portal.');
        const signInUrl = isTechnicianSession || window.location.pathname.startsWith('/tech')
          ? technicianSignIn
          : isPortal ? 'https://wrenchlab.net/SignIn' : '/workspace-login';
        window.location.href = signInUrl;
      }
      return Promise.reject(error);
    }

    if (status === 403 && data?.error === 'SESSION_LIMIT_REACHED') {
      let slug = '';
      try { slug = sessionStorage.getItem('wl.pendingSlug') ?? ''; } catch { /* ignore */ }
      if (!slug) { try { slug = localStorage.getItem('wl.workspace') ?? ''; } catch { /* ignore */ } }
      if (slug) {
        try { localStorage.setItem('wl.workspace', slug); } catch { /* ignore */ }
        try { sessionStorage.removeItem('wl.pendingSlug'); } catch { /* ignore */ }
      }
      const params = new URLSearchParams();
      if (slug) params.set('slug', slug);
      if (data.message) params.set('message', data.message);
      if (data.retryAfterMinutes) params.set('retryAfterMinutes', String(data.retryAfterMinutes));
      if (data.limit) params.set('limit', String(data.limit));
      const query = params.toString();
      window.location.href = query ? `/session-limit?${query}` : '/session-limit';
      return Promise.reject(error);
    }

    if (status === 403 && data?.error === 'TENANT_SUSPENDED') {
      useAuthStore.getState().setAccountSuspended(true);
      return Promise.reject(error);
    }

    if (status === 403 && data?.error === 'BILLING_ISSUE') {
      useAuthStore.getState().setBillingInfo(data.billingStatus ?? null, null, null);
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export function useApi() {
  return apiClient;
}
